// Resilient module loader.
//
// Eight subsystems are authored independently. Boot lets the game come up with
// whatever is present and degrade cleanly for whatever isn't, instead of a blank
// screen when one import throws. Failures are surfaced on the boot overlay.

const status = document.getElementById('boot-status');
const fill = document.getElementById('boot-fill');
const errBox = document.getElementById('boot-err');

export const bootReport = { loaded: [], failed: [] };

let step = 0;
let totalSteps = 1;

export function setStepCount(n) { totalSteps = Math.max(1, n); }

/**
 * Wall-clock cost of each boot phase, in order.
 *
 * A product gate wants "menu usable within 1.5 s" and this build takes 3.6-4.6 s
 * on Windows. Network is not the cause — 490 KB, DOM ready at 1.2 s — so the
 * remainder is boot work, and no one could say WHICH work. Recording it here
 * (and surfacing it through AXIOM_QA) turns that into a number per phase instead
 * of an argument.
 */
export const bootTimings = [];
let _lastMark = typeof performance !== 'undefined' ? performance.now() : 0;
let _lastLabel = 'start';

export function progress(label) {
  if (typeof performance !== 'undefined') {
    const now = performance.now();
    bootTimings.push({ phase: _lastLabel, ms: +(now - _lastMark).toFixed(1), at: +now.toFixed(1) });
    _lastMark = now;
    _lastLabel = label;
  }
  return _progress(label);
}

function _progress(label) {
  step++;
  if (status) status.textContent = label;
  if (fill) fill.style.width = `${Math.min(100, (step / totalSteps) * 100).toFixed(1)}%`;
}

const _shownErrors = new Set();

export function bootError(name, err) {
  bootReport.failed.push({ name, err: String(err && err.stack || err) });
  console.error(`[boot] ${name} failed:`, err);
  if (!errBox) return;
  // One CAUSE, one line. A single root failure surfaces through every subsystem
  // that depended on it — a refused WebGL context is reported by the Engine and
  // then again by boot — and printing the same paragraph twice reads like two
  // separate faults on the one screen a blocked player has to act on.
  const msg = String(err && err.message || err);
  if (_shownErrors.has(msg)) return;
  _shownErrors.add(msg);
  errBox.textContent += `⚠ ${msg}\n`;
}

export const bootMissing = [];

// Dynamic import that never rejects. `loader` may be undefined when the module
// does not exist yet — that is a *missing* subsystem (expected during
// incremental development), distinct from one that exists but throws.
export async function safeImport(name, loader) {
  if (typeof loader !== 'function') {
    bootMissing.push(name);
    return null;
  }
  try {
    const mod = await loader();
    bootReport.loaded.push(name);
    return mod;
  } catch (err) {
    bootError(name, err);
    return null;
  }
}

// Construct guarded — returns null instead of throwing.
export function safeNew(name, fn) {
  try {
    const v = fn();
    return v;
  } catch (err) {
    bootError(name, err);
    return null;
  }
}

// Wrap a per-frame update so one bad subsystem can't kill the loop.
// After 8 consecutive throws the system is disabled for the session.
export function guardUpdate(name, fn) {
  let strikes = 0;
  let dead = false;
  return (dt) => {
    if (dead) return;
    try { fn(dt); strikes = 0; }
    catch (err) {
      if (++strikes === 1) console.error(`[update] ${name}:`, err);
      if (strikes >= 8) {
        dead = true;
        console.error(`[update] ${name} disabled after repeated failures.`);
        bootError(name, err);
      }
    }
  };
}

export function hideBoot() {
  const el = document.getElementById('boot');
  if (el) el.classList.add('hidden');
}

export function showStart(onStart) {
  const btn = document.getElementById('boot-start');
  if (!btn) { onStart(); return; }
  btn.classList.add('on');
  if (status) status.textContent = '準備完了';
  let padRAF = 0;
  let padHeld = false;
  const clickGo = () => go('pointer');
  const go = (source = 'pointer') => {
    btn.removeEventListener('click', clickGo);
    window.removeEventListener('keydown', keyGo);
    cancelAnimationFrame(padRAF);
    onStart(source);
  };
  const keyGo = (e) => { if (e.code === 'Space' || e.code === 'Enter') go('keyboard'); };
  const pollPad = () => {
    let held = false;
    try {
      const pad = Array.from(navigator.getGamepads?.() || []).find(Boolean);
      held = !!(pad && (pad.buttons?.[0]?.pressed || pad.buttons?.[9]?.pressed));
    } catch { held = false; }
    if (held && !padHeld) { go('gamepad'); return; }
    padHeld = held;
    padRAF = requestAnimationFrame(pollPad);
  };
  btn.addEventListener('click', clickGo, { once: true });
  window.addEventListener('keydown', keyGo);
  padRAF = requestAnimationFrame(pollPad);
}
